import {_, computed, observable, autorun} from '@api'
import {DateRange} from '@blueprintjs/datetime'
import {Orientation} from '@material-ui/core'
import {CatalogLibrariesPanelSettings, IHeroCardsOverrides, layoutSettingDescriptors} from '@modules'
import {IRenderPDFServerSidePayload} from '@modules/pdf'
import {CatalogCardImagesView, CatalogStore, globalStores, IGroupOrderBy, IIndividualGroupSettings, PdfExportConfig, PdfLocalInfo, CommonQueryParams, IndividualCatalogPDFSerializableSettings, ClientPdfSerializableSettings} from '@stores'
import {IFlexLayoutCatalogRootConfig} from '@stores/catalog/flexLayout'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {IVLPHierarchySelection} from '@stores/vlp'
import {ImageRatio} from '@utils'
import {IJsonModel} from '@flexlayout'
import {Base64} from 'js-base64'
import {runInAction, trace} from 'mobx'
import {deepObserve} from 'mobx-utils'
import {ISettingsForGroupLevel, ReportPDFSerializableSettings} from '.'

/// region Interfaces

/// region Settings

const settingsToCopyFromGlobalCatalogStore: Array<keyof ReportCatalogSerializableSettings> = [/*'groupBy', 'detailLoc',*/ 'lang']

export interface CardImageSourceToolbarSettings {
  location?: 'header' | 'footer'
  compact?: boolean
}

export type CardImageSourceSettings = {
  ids?: Array<string>
  view?: CatalogCardImagesView
  toolbar?: CardImageSourceToolbarSettings

  scaleOnHover?: number
  stackedCards?: {
    blend?: boolean
    spread?: number
    opacity?: number,
    maxVisible?: number
    // imagesPerItem?: number
  }
  grid?: {
    orientation?: Orientation
    rows?: number
    columns?: number
  }
}

export type CatalogViewMode = 'card' | 'report' | 'table' | 'pdf'

export type CatalogCardSpacingSetting = { value: number, min: number, max: number }

export type ICardViewFieldSettings = {
  collapsed?: boolean
  show?: boolean
}

export type ICardViewSettings = { [field: string]: ICardViewFieldSettings }

export type CardGridLines = 'none' | 'horizontal' | 'vertical' | 'both'

export interface IGroupBySettings {
  flatModeColumns?: number
  showEmptyGroups?: boolean
  compact?: boolean
  showOnlyExpanded?: boolean
  showExpandedFirst?: boolean
  noWrap?: boolean
  hierarchy?: string[]
  columnView?: boolean
  fixedWidth?: boolean
  expandAllLevel?: number
  groupView?: 'flat' | 'card' | 'pivot',
  orderBy?: Array<IGroupOrderBy>
  individualGroupSettings?: Partial<IIndividualGroupSettings>
  byGroup?: { [id: string]: Partial<IIndividualGroupSettings> }

  //groupingSettings?: Partial<IGroupingSettings>

  heroCards?: {
    hideCollapsed?: boolean,
    show?: boolean
  }

  showSwatches?: boolean
  hideCatalogItemsOnSwatchHover?: boolean
  showSwatchesWhenCollapsed?: boolean
  depth?: number
  settingsForLevel?: { [level: number]: Partial<ISettingsForGroupLevel> }
}

export type AssortmentSettingsPerCard = { [key: string]: AssortmentCardSettings }

export type AssortmentCardSettings = {
  hideFooter?: boolean
  hideHeader?: boolean
  hideSecondLevel?: boolean
  zoomed?: boolean
  heroCards?: {
    show?: boolean
    /*
    If the layout is limited to ## cards but the current group has more, show them?
    Do this in local state so it resets on reload unless we have a GUI way to undo
     */
    showAll?: boolean
    overrides?: IHeroCardsOverrides

    secondLevel: {
      selections?: { [heroId: string]: { selected?: number } }
    }
  }

  swatches?: {
    selected?: string
  }
  perspective?: string
  expanded?: boolean
}

export const DEFAULT_ASSORTMENT_CARD_SETTINGS: AssortmentCardSettings = {
  heroCards: {
    show: true,
    showAll: false,
    secondLevel: {
      selections: {}
    },
    overrides: {
      orders: {},
      fullWidth: {},
      subdivide: {},
      combinedShapes: []
    }
  },
  swatches: {
    selected: null
  },
  perspective: null,
  expanded: false
}

export interface AssortmentLayoutSettings {
  rootFontSize?: number
  columns?: number
  swatchesPerColumn?: number
  assortmentPadding?: number
  heroGap?: number
  heroesPerAssortment?: number,
  columnWidth?: number
  heroHeight?: number
  secondLevelHeight?: number
}

export interface AssortmentSettings {
  hideAllFooters?: boolean
  hideAllHeaders?: boolean
  hideAll2ndLevel?: boolean
  groupingBehavior?: 'inside' | 'outside'
  id?: string
  columnView?: boolean
  fluidCards?: boolean
  byCard?: AssortmentSettingsPerCard
  orders?: { [field: string]: 'asc' | 'desc' }
  hero?: {
    removeCombinedCommonWords?: boolean
    /**
     * User can collapse heroes to show just the headers + swatches in gallary
     */
    show?: boolean

    fullWidthRows?: number[]
    hideEmptyRows?: boolean
    secondLevel: {
      wrap?: boolean
    }
  }
  swatches?: {
    show?: boolean

    globalSelected?: string
  },
  layout?: AssortmentLayoutSettings
}

export type HorzAlign = 'left' | 'center' | 'right'

export interface SeasonSelectorSettings {
  showZeroValues?: boolean
}

export interface IPresentationCatalogPage {
  gridColumns?: number
  autoColumns?: boolean

  /**
   * Image snapshot of rendered page
   */
  snapshotImage?: string
  size: { width: number, height: number }
  title?: string
  content?: string

  info?: IPresentationCatalogPageType
}

export type IPresentationCatalogPageType = {
  type: 'group', group: string
}

export interface IPresentationCatalogSettings {
  showScrollbars?: boolean
  pageNumber?: number
  showNavigator?: boolean
  zoom?: number
  show?: boolean
  fullScreen?: boolean
  pages?: Array<IPresentationCatalogPage>
}

export interface CatalogDetailsSettings {
  modal?: {
    orientation?: 'horz' | 'vert'
  }
}

export type VerticalAlign = 'top' | 'center' | 'bottom'

/// region Report Level serializable settings - flexLayout, overall pdf, etc
export type ReportCatalogSerializableSettings = {
  flexLayout?: {
    activeTab?: string
    model?: IJsonModel,
    maximizedTab?: string
  }
  lang?: string

  reportPdf?: ReportPDFSerializableSettings
}


export type CatalogSerializableSettings = {
  presentation?: IPresentationCatalogSettings
  activeSearchText?: string
  seasonSelectors?: { [field: string]: SeasonSelectorSettings }
  detailLoc?: 'sidebar' | 'modal'
  imageRatio?: ImageRatio
  viewMode?: CatalogViewMode
  perspective?: string,
  imageSource?: CardImageSourceSettings
  details?: CatalogDetailsSettings

  align?: HorzAlign,
  verticalAlign?: VerticalAlign
  focusedCatalogItemId?: string
  levelOfDetail?: number
  selectedCatalogIds?: Array<string>
  preset?: string
  assortment?: AssortmentSettings
  cardView?: ICardViewSettings
  autoLoadMoreCards?: boolean
  showSelectedFirst?: boolean

  /**
   * Size of catalog item cards in main card view
   */
  cardSize?: number
  cardSizePercentOverride?: number
  cardSpacing?: number

  showInlineCatalogItemsFirst?: boolean,
  showOnlyInlineCatalogItems?: boolean,
  cardViewOrderOverride?: { [workspace: string]: string[] },
  showOnlyItemsWithChanges?: boolean
  autoSaveInlineChanges?: boolean
  showCardLabels?: boolean
  showOnlyOfflineItems?: boolean

  /**
   * Try to navigate to the corresponding hierarchy for the focusedCatalogItemId upon catalog load
   */
  focusToHierarchy?: boolean

  vlp?: {
    selected?: IVLPHierarchySelection
  }

  dataGrid?: {
    lockedColumns?: Array<{ id: string, locked: 'start' | 'end' | true | false }>
  }

  noSubscribe?: boolean,

  perItem?: { [id: string]: { lod?: number, unsavedChanges?: any } }

  toolbarSwitches?: { [field: string]: boolean }
  borders?: { cardBorders?: boolean, gridLines?: CardGridLines }
  showNestedFieldsOnTopLevelCard?: boolean
  freezeIds?: string[]

  groupBy?: IGroupBySettings

  sync?: {
    seasons?: Array<string>
  }

  sort?: {
    depth?: number
  }

  showTargetsToolbar?: boolean
  aggregateDetailsGroupPath?: Array<string[]>,
  aggregateDetailsSelectedSections?: { [section: string]: Array<string> }
  aggregateLocation?: 'sidebar' | 'inline'
  showAggs?: boolean
  detailAggs?: {
    visible?: { [field: string]: boolean }
  },
  showAggDetailsWhenCollapsed?: boolean
  searcherSearchText?: string

  // stackView?: boolean,
  // stackGroups?: boolean

  groupSectionFieldsOnCard?: { [section: string]: boolean }
  toolbarItems?: ShowToolbarItemsSettings

  catalogLibrariesPanel?: CatalogLibrariesPanelSettings

  pdf?: IndividualCatalogPDFSerializableSettings
  clientPdf?: ClientPdfSerializableSettings
  changelog?: ChangeLogSettings
}

export type ICatalogUserSettings = ReportCatalogSerializableSettings | CatalogSerializableSettings

export type ChangelogFieldType = string | number | boolean | Date

export type ChangeLogSettings = {
  shouldApplyFilters?: boolean
  filters?: Record<string, { equals?: Array<ChangelogFieldType> | DateRange, excluded?: Array<ChangelogFieldType> }>
  isTableView?: boolean
  expanded?: ChangeLogExpandedType
  filterSelected?: any //TODO change it later
  newLook?: boolean
  trackCurrentWorkspace?: boolean
}

export type ChangeLogExpandedType = {
  [key: number]: boolean
}

export type ShowToolbarItemsSettings = {
  topToolbar?: { visible?: boolean },
  footerToolbar?: { visible?: boolean },
  imageSources?: { visible?: boolean },
  viewOptions?: { visible?: boolean },
  sliders?: {
    visible?: boolean,
    location?: 'top' | 'secondary' | 'footer'
  },
  filterChips?: { visible?: boolean }
}

export const defaultGroupBySettings: IGroupBySettings = {
  showEmptyGroups: false,
  flatModeColumns: 3,
  depth: 0,
  groupView: 'card',
  compact: false,
  columnView: false,
  showOnlyExpanded: false,
  showExpandedFirst: false,
  noWrap: false,
  hierarchy: [],
  fixedWidth: true,
  expandAllLevel: 0,
  orderBy: [],
  heroCards: {
    hideCollapsed: true,
    show: false
  },
  showSwatches: false,
  hideCatalogItemsOnSwatchHover: false,
  showSwatchesWhenCollapsed: false,
  individualGroupSettings: {
    childColumns: 2,
    columnView: false,
    fullWidth: false,
    singleLine: false,
    fullHeight: false,
    orientation: 'vertical',
    stackView: 'collapsed',
    expanded: false,
    showOnlyOfflineItems: false,
    showOnlyOnlineItems: false,
    infoPanelActive: false,
    levelOfDetail: 0,
    perspective: null,
    forceShowInlineAggregationDetails: false
  },
  settingsForLevel: {
    0: {
      swatchSize: 32,
      twoDColumnView: false,
      swatches: [],
      subtitleFields: []
      // heroCardField: 'collection'
    },
    1: {
      swatchSize: 32,
      twoDColumnView: false,
      swatches: [],
      subtitleFields: []
    },
    2: {
      swatchSize: 32,
      twoDColumnView: false,
      swatches: [],
      subtitleFields: []
    }
  }
}

export const DEFAULT_REPORT_CATALOG_SETTINGS: Partial<ReportCatalogSerializableSettings> = {
  reportPdf: {
    includeCoverPage: true,
    includeAllSectionQRCodes: true,
    generatePDFPerCatalog: true,
    includeQRCode: true,
    orientation: 'portrait',
    pageSize: 'A4'
  },
  flexLayout: {
    model: undefined
  },

}

export const DEFAULT_CATALOG_USER_SETTINGS: Partial<CatalogSerializableSettings> & Partial<ReportCatalogSerializableSettings> = {
  details: {
    modal: {
      orientation: 'horz'
    }
  },
  pdf: {
  },
  clientPdf: {},
  presentation: {
    show: false,
    pages: [],
    showNavigator: false,
    showScrollbars: true,
    zoom: .6,
    pageNumber: 1,
    fullScreen: false
  },
  activeSearchText: '',
  focusedCatalogItemId: null,
  selectedCatalogIds: [],
  align: 'left',
  verticalAlign: 'center',
  levelOfDetail: 0,
  showSelectedFirst: true,
  autoLoadMoreCards: true,
  preset: undefined,
  assortment: {
    id: undefined,
    orders: {},
    fluidCards: false,
    columnView: false,
    groupingBehavior: 'inside',
    byCard: {},
    hero: {
      show: true,
      fullWidthRows: [0],
      secondLevel: {wrap: true}
    },
    swatches: {
      show: true
    },
    layout: _.keys(layoutSettingDescriptors).reduce((acc, k) => ({...acc, [k]: layoutSettingDescriptors[k].defaultValue}), {})
  },
  cardView: {},

  cardSize: undefined,
  cardSpacing: 1,
  showInlineCatalogItemsFirst: false,
  showOnlyInlineCatalogItems: false,
  showOnlyItemsWithChanges: false,
  cardViewOrderOverride: {},
  showOnlyOfflineItems: false,
  showCardLabels: true,
  autoSaveInlineChanges: true,
  imageRatio: 'square',
  detailLoc: 'modal',
  viewMode: 'card',
  seasonSelectors: {},
  perspective: undefined,

  imageSource: {
    //ids: [], // Otherwise we'll never use defaults
    view: 'first',
    scaleOnHover: 1,
    grid: {
      rows: 3,
      columns: 3,
      orientation: 'horizontal'
    },
    stackedCards: {
      opacity: 1,
      blend: false,
      spread: .5,
      maxVisible: 20
      // imagesPerItem: 999
    },
    toolbar: {
      compact: false,
      location: 'footer'
    }
  },

  groupBy: _.cloneDeep(defaultGroupBySettings),
  vlp: {selected: {}},
  dataGrid: {
    lockedColumns: []
  },
  perItem: {},
  noSubscribe: false,
  sort: {
    depth: 1
  },
  toolbarSwitches: {},
  borders: {
    cardBorders: false,
    gridLines: 'none'
  },
  sync: {
    seasons: []
  },

  showNestedFieldsOnTopLevelCard: false,
  freezeIds: [],
  showTargetsToolbar: false,
  aggregateDetailsGroupPath: [],
  groupSectionFieldsOnCard: {},
  aggregateLocation: 'inline',
  aggregateDetailsSelectedSections: {},

  searcherSearchText: '',
  showAggs: false,
  showAggDetailsWhenCollapsed: false,
  detailAggs: {visible: {}},
  toolbarItems: {
    topToolbar: {visible: true},
    footerToolbar: {visible: true},
    imageSources: {visible: true},
    sliders: {visible: true, location: 'footer'},
    viewOptions: {visible: true},
    filterChips: {visible: true}
  },

  catalogLibrariesPanel: {
    sections: {}
  },
  changelog: {
    expanded: {},
    filterSelected: [],
    newLook: true,
    shouldApplyFilters: false,
    trackCurrentWorkspace: true,
    filters: {}
  }
}

/// endregion

/// region Query Parameters

export interface CatalogQueryParams extends CommonQueryParams {
  /**
   * Fields to display on cards, in the PDF, in the table, etc
   * Too big, leads to 431 errors
   */
  // cardView?: string | undefined

  ws_settings?: string | undefined // json
  //focused?: string | undefined // expensive, causes performance problems when url changes on every select

  /**
   * Base64 encoded settings for individual groups in the hierarchy
   *
   * See YFTS-6861
   * We keep a minimum amount of basic state in the URL and the rest in local storage to avoid running out of URL space (and generally unwieldly URLs)
   *
   * Things for the URL:
   *    Groups, selected fields, workspace, deployment, top level group sort orders
   */
  groupBy?: string | undefined
  groupBy_orders?: string | undefined  // Group by orders  // used to be per group settings like isExpanded, showing aggregate details, # of columns to render in the UI, level of detail, etc - now in localStorage
}

export interface CatalogQueryParamsParsed {
  /**
   * Fields to display on cards, in the PDF, in the table, etc
   */
  // cardView?: {}

  ws_settings?: Partial<ICatalogUserSettings>

  /**
   * Base64 encoded settings for individual groups in the hierarchy
   *
   * See YFTS-6861
   * We keep a minimum amount of basic state in the URL and the rest in local storage to avoid running out of URL space (and generally unwieldly URLs)
   *
   * Things for the URL:
   *    Groups, selected fields, workspace, deployment, top level group sort orders
   */
  groupBy?: any
  groupBy_orders?: string | undefined  // Group by orders  // used to be per group settings like isExpanded, showing aggregate details, # of columns to render in the UI, level of detail, etc - now in localStorage
}

/// endregion

/// endregion

/// endregion
export class CatalogSettingsStore {
  constructor(public catalog: CatalogStore) {
    const {stores} = catalog
    this.settings = _.cloneDeep(catalog.isGlobalStore ? DEFAULT_REPORT_CATALOG_SETTINGS : DEFAULT_CATALOG_USER_SETTINGS) as any

    // Only run if we're the global store
    synchronizeStores(() => {
      // if (stores.nav.queryParams.cardView == null && !this.localParams.hasOwnProperty('cardView')) {
      //   catalog.setCardView(catalog.defaultCardView)
      // }

      const {isGlobalStore, reports} = stores

      if (!isGlobalStore) {
        const toDispose = [
          this.autorun_updateNonDefaultSettings(),
          autorun(() => {
            // trace()
            if (this.catalog.hasLoadedConfig) {
              const {flexLayoutCatalogRoot, extra, catalog: {workspaceConfig}} = stores,
                globalSettingsToCopy = _.pick(globalStores.catalog.settings, [...settingsToCopyFromGlobalCatalogStore/*, catalog.isCurrentWorkspace ? 'imageSource' : null*/].filter(_.identity))

              if (!this.urlProcessed) {
                const config = extra?.tab?.config ?? flexLayoutCatalogRoot?.jsonNode?.config
                if (config) {
                  runInAction(() => {
                    // Deserialize layout options from { config } portion of flex layout
                    // const config = extra.tab.config as IFlexLayoutCatalogRootConfig

                    // if (config) {
                    const flexConfigCatalogSettings = config.settings?.catalog ? _.cloneDeep(config.settings?.catalog) : null

                    if (!_.isEmpty(flexConfigCatalogSettings?.imageSource?.ids)) {
                      const validIds = (flexConfigCatalogSettings?.imageSource?.ids ?? workspaceConfig?.catalog?.features?.imageSources?.default?.ids ?? [])?.filter(id => catalog.imageSourceById[id] != null)
                      _.merge(flexConfigCatalogSettings, {imageSource: {ids: validIds}})
                    }

                    runInAction(() => {
                      _.merge(this.settings, _.cloneDeep(_.omit(this.defaultSettings, 'cardView')), flexConfigCatalogSettings)
                    })
                    // }
                  })
                } else {
                  runInAction(() => {
                    _.assign(this.settings, _.cloneDeep(this.defaultSettings))
                  })
                }
                // Use options from flex layout config

                this.urlProcessed = true
              }

              runInAction(() => {
                Object.assign(this.settings, globalSettingsToCopy)
              })
            }
          }, {name: `Copy settings from the global catalog store / flex layout tab config into our instance level settings stores`}),
          autorun(() => {
            const {catalog: {hasFinishedFirstLoad, offlineItemCount}, catalogSettings, catalogSettings: {showOnlyOfflineItems}} = this
            if (hasFinishedFirstLoad && showOnlyOfflineItems && offlineItemCount === 0) {
              this.catalogSettings.showOnlyOfflineItems = false
            }
          })
        ]
        this.catalog.toDispose.push(...toDispose)
      } else { // Global store - singleton
        DEFAULT_CATALOG_USER_SETTINGS.cardSize = catalog.defaultCardSize
        this.urlProcessed = true
        // autorun(this.autorun_loadCatalogSettingsFromCurrentReport, {name: 'Load catalog settings from current report'})
      }

      autorun(() => {
        const {workspaceConfig} = catalog
        if (this.urlProcessed && workspaceConfig) {
          const {features: {imageSources, actionButtons}, imageSourceById} = catalog,
            {settings} = this,
            validIds = (settings?.imageSource?.ids ?? workspaceConfig?.catalog?.features?.imageSources?.default?.ids ?? [])?.filter(id => imageSourceById[id] != null)

          runInAction(() => {
            const {settings} = this

            if (!_.isEqual(settings.imageSource?.ids, validIds)) {
              settings.imageSource.ids = validIds.slice()
            }

            if ((!workspaceConfig.catalog?.reportView && settings?.viewMode === 'report')
                || (!actionButtons?.tableView && settings?.viewMode === 'table')) {
              _.merge(settings, {catalog: {viewMode: 'card'}})
            }

            if (imageSources?.multiple === false) {
              _.merge(settings, {catalog: {imageSource: {view: 'first'}}})
            }

            // Don't do things this way, it leads to weird behaviors where you don't know the default is overriding things, need the GUI to show that something is a 'default' or simply promote the 'default' to first in the 'All' list
            // if (_.isEmpty(settings.imageSource.ids) && !_.isEmpty(configImageSourceIds)) {
            //   settings.imageSource.ids = configImageSourceIds
            // }
          })
        }
      }, {name: `Turn off certain settings when config changes in a way that makes them invalid`})
    })
  }

  get catalogSettings() {
    return this.settings as CatalogSerializableSettings
  }

  get reportSettings() {
    return this.settings as ReportCatalogSerializableSettings
  }

  // autorun_loadCatalogSettingsFromCurrentReport = () => {
  //   const {catalog, catalog: {stores: {reports}}} = this
  //   if (!catalog.isGlobalStore) return
  //
  //   const {settingsFromReport, urlProcessed} = this
  //   if (!urlProcessed || !this.settings.flexLayout) {
  //     runInAction(() => {
  //       if (!settingsFromReport) {
  //         console.log(`Waiting to load settings from report`)
  //         return
  //       }
  //
  //       DEBUG && console.log(`Loading settings from current report...`)
  //       //productCardViewStore && c_page && (this.groupBy.productCardViewStore.timesClicked == parseInt(c_page))
  //
  //       // console.log(toJS({defaultCatalogUserSettings, settingsFromLocalStorage, qp: qp.ws_settings}))
  //
  //       // console.log({settingsFromReport, fixedQueryParams })
  //       this.settings = _.merge({}, defaultCatalogUserSettings, settingsFromReport)
  //
  //       ui.setLang(catalog.settings.lang)
  //
  //       this.urlProcessed = true
  //
  //       DEBUG && console.log(`Loaded catalog settings report ${reports.currentReport?.id}`, toJS(this.settings), {catalog})
  //     })
  //   }
  // }

  get defaultSettings() {
    const {catalog: {isGlobalStore}} = this

    if (isGlobalStore) {
      return _.cloneDeep(DEFAULT_REPORT_CATALOG_SETTINGS)
    } else {
      return _.cloneDeep({...DEFAULT_CATALOG_USER_SETTINGS, cardView: this.catalog.cv.defaultCardView})
    }
  }

  @observable.ref nonDefaultSettings: Partial<CatalogSerializableSettings> | Partial<ReportCatalogSerializableSettings>

  autorun_updateNonDefaultSettings = () => autorun(() => {
    const {defaultSettings, settings, catalog: {isGlobalStore}} = this

    this.catalog.toDispose.push(
      deepObserve(settings, () => {
      this._updateNonDefaultSettings()
    }))
  })

  _updateNonDefaultSettings = _.debounce(() => {
    const {settings, defaultSettings, catalog: {isGlobalStore}} = this,
      ignoreForPreset = new Set<keyof CatalogSerializableSettings>([]),
      alwaysIncludeInPreset = new Set<keyof CatalogSerializableSettings>(['vlp', 'preset'/*, 'assortment'*/])

    if (isGlobalStore) {
      this.nonDefaultSettings = _.pick(settings as ReportCatalogSerializableSettings, ['flexLayout', 'lang', 'pdf'])
      return
    }

    let r : CatalogSerializableSettings = _.pickBy(settings, (v, k: keyof CatalogSerializableSettings) => {
      if (alwaysIncludeInPreset.has(k)) return true
      else if (ignoreForPreset.has(k)) return false
      else {
        return !_.isEqual(settings[k], defaultSettings[k])
      }
    }) as CatalogSerializableSettings

    if (r.cardView) {
      r.cardView = _.pickBy(r.cardView, (v, k) => {
        return v.show
      })
    }

    this.nonDefaultSettings = r
  }, 500)

  // @computed get settingsFromReport(): Partial<ICatalogUserSettings> {
  //   const {catalog: {isGlobalStore, stores: {reports: {currentReport}}}} = this
  //
  //   if (!isGlobalStore) debugger
  //
  //   if (!currentReport) return null
  //
  //   const defaultSettings = defaultCatalogUserSettings,
  //     localData = currentReport.settingsParsed
  //
  //   // console.log(`localData`, localData)
  //
  //   let r = _.merge({}, defaultSettings, localData)
  //
  //   // Since the last time we serialized settings we might have changed the shape of our configuration, verify what we have is still valid
  //
  //   recursivelyFixUpExistingObject(r, defaultSettings)
  //
  //   return r
  // }

  @computed get cardViewToggledOnFields() {
    const {cardView} = this.catalog
    return Object.keys(cardView || {}).filter(k => cardView[k]?.show).sort()
  }

  queryParamsFor = (override: Partial<ICatalogUserSettings> = {}) => {
    return {}
    //
    // const {catalog: {groupBy, isGlobalStore, stores: {nav}, flexLayout: {flexEnabled}}} = this
    //
    // if (flexEnabled && isGlobalStore) return {}
    //
    // const settings = _.merge({}, _.pick(this.settings, _.keys(defaultCatalogUserSettings)), override)
    //
    // // trace()
    //
    // // console.log(settings)
    // const nextQueryParams = jsondiff.diff(settings, defaultCatalogUserSettings)
    //
    // // Only pick query params keys above instead of all
    // // _.keys(defaultCatalogUserSettings_LocalStorage).filter(k => !_.has(override, k)).forEach(k => delete nextQueryParams[k])
    //
    // const catalogSettings = _.isEmpty(nextQueryParams) ? null : Base64.encodeURI(stringify(_.keys(nextQueryParams).reduce((acc, k) => _.assign(acc, {[k]: toJS(settings[k])}), {})))
    //
    // let r: Partial<DashboardQueryParams> = removeUndefinedsNested({
    //   ...groupBy.queryParams,
    //   ws_settings: _.isEmpty(catalogSettings) ? null : catalogSettings
    // })
    //
    // return r
  }

  @observable urlProcessed = false

  @observable settings: Partial<CatalogSerializableSettings>
}

export const recursivelyFixUpExistingObject = (o: any, defaults: any) => {
  _.keys(defaults).forEach(k => {
    const defaultValue = defaults[k]
    if (_.isNull(defaultValue) || _.isUndefined(defaultValue)) {
    } else {
      if (_.isUndefined(o[k])) {
        o[k] = defaultValue
      } else if (typeof o[k] != typeof defaultValue) {
        o[k] = defaultValue
      }

      // console.log(k, o[k], defaultValue)
      if (_.isPlainObject(defaultValue)) {
        if (!_.isPlainObject(o[k])) {
          o[k] = defaultValue
        } else {
          recursivelyFixUpExistingObject(o[k], defaultValue)
        }
      }
    }
  })

  return o
}


// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogSettingsStore.ts